import {
  handleSkippedCalls,
  initializeAndMakeCall,
  moveToNextCallCampaignStep,
  toGetCallCampaignById,
  getFromPhoneNumber,
} from "@/src/utils/callsUtils.js";
import { dialerTypes } from "@/src/constants/callConstants.js";
import router from "@/src/router/index.js";
import eventBus from "@/src/utils/eventBus.js";
import { bannerComponentTypes } from "@/src/constants/bannerConstants.js";
import { callCampaignStatuses } from "@/src/constants/callConstants.js";

export default {
  namespaced: true,

  state: {
    currentCampaign: {
      isActive: false,
      campaignId: null,
      details: null,
      status: null,
    },
    skippedCallsCount: 0,
    isRecordingEnabled: false,
    isCampaignPageOpen: false,
    isSkipCooldown: false,
    callOngoing: false,
    drawerVisible: false,
    campaignCallSid: null,
  },

  mutations: {
    SET_CURRENT_CAMPAIGN(
      state,
      { isActive, campaignId = null, details = null, status = null },
    ) {
      state.currentCampaign = {
        isActive,
        campaignId,
        details,
        status,
      };
    },

    UPDATE_CAMPAIGN_DETAILS(state, details) {
      state.currentCampaign.details = details;
    },

    UPDATE_CAMPAIGN_ID(state, id) {
      state.currentCampaign.campaignId = id;
    },

    UPDATE_CAMPAIGN_STATUS(state, status) {
      state.currentCampaign.status = status;
    },

    UPDATE_IS_ACTIVE(state, active) {
      state.currentCampaign.isActive = active;
    },

    INCREMENT_SKIPPED_CALLS(state) {
      state.skippedCallsCount++;
    },

    RESET_SKIPPED_CALLS(state) {
      state.skippedCallsCount = 0;
    },

    SET_IS_RECORDING_ENABLED(state, value) {
      state.isRecordingEnabled = value;
    },

    SET_IS_CAMPAIGN_PAGE_OPEN(state, value) {
      state.isCampaignPageOpen = value;
    },

    SET_SKIP_COOLDOWN(state, value) {
      state.isSkipCooldown = value;
    },

    UPDATE_CALL_ONGOING(state, value) {
      state.callOngoing = value;
    },

    SET_DRAWER_VISIBLE(state, value) {
      state.drawerVisible = value;
    },
    SET_CAMPAIGN_CALL_SID(state, value) {
      state.campaignCallSid = value;
    },
  },

  actions: {
    async startCampaign({ commit, dispatch, state }, campaignId) {
      try {
        if (state.currentCampaign.status === callCampaignStatuses.COMPLETED) {
          return;
        }
        handleSkippedCalls().then();
        const details = await toGetCallCampaignById(campaignId);
        if (details.status === callCampaignStatuses.COMPLETED) {
          await dispatch("endCampaign", details);
          commit("UPDATE_IS_ACTIVE", false);
          return;
        }
        commit("SET_CURRENT_CAMPAIGN", {
          isActive: true,
          campaignId,
          details: { ...details, status: callCampaignStatuses.IN_PROGRESS },
          status: callCampaignStatuses.IN_PROGRESS,
        });

        await Promise.all([
          dispatch("dynamicBanner/updateShowBanner", true, { root: true }),
          dispatch(
            "dynamicBanner/updateBannerComponentType",
            bannerComponentTypes.CALL_CAMPAIGNS,
            { root: true },
          ),
        ]);

        eventBus.emit("call-campaign-start", campaignId);
        await dispatch("processNextCall");
      } catch (error) {
        console.error("An error occurred while starting campaign: ", error);
      }
    },

    async updateCampaignDetails(
      { commit, rootState, state, dispatch },
      details,
    ) {
      try {
        if (details.status === callCampaignStatuses.COMPLETED) {
          await dispatch("endCampaign", details);
          commit("UPDATE_IS_ACTIVE", false);
          return;
        }
        commit("UPDATE_CAMPAIGN_DETAILS", details);
        commit("UPDATE_CAMPAIGN_ID", details?.id);
        commit("UPDATE_CAMPAIGN_STATUS", details?.status);
        if (
          (details?.status === callCampaignStatuses.PAUSED ||
            details?.status === callCampaignStatuses.ENDED) &&
          !rootState.calls.isCallConnected
        ) {
          commit("UPDATE_IS_ACTIVE", false);
        }
      } catch (error) {
        console.error(
          "An error occurred while updating campaign details: ",
          error,
        );
      }
    },

    async processNextCall({ state, commit, dispatch }) {
      if (
        state.currentCampaign.status === callCampaignStatuses.COMPLETED ||
        state.currentCampaign.status === callCampaignStatuses.ENDED ||
        state.currentCampaign.status === callCampaignStatuses.PAUSED
      ) {
        return;
      }
      if (!state.currentCampaign.isActive) return;
      if (state.callOngoing) return;

      const response = await moveToNextCallCampaignStep(
        state.currentCampaign.campaignId,
      );

      if (response?.success) {
        const data = response?.entity;
        if (data?.length === 0) {
          await dispatch("refreshCampaignDetails");
          return;
        }

        if (
          state.currentCampaign.details?.campaign_type ===
          dialerTypes.POWER_DIALER
        ) {
          commit("UPDATE_CALL_ONGOING", true);
        }

        const entityData = data[0];
        if (!entityData) {
          await dispatch("refreshCampaignDetails");
          return;
        }
        
        const entityType = entityData.lead_status ? 'lead' : 'contact';
        const fromPhoneNumber = await getFromPhoneNumber({
          campaignDetails: state.currentCampaign.details,
          toPhoneNumber: entityData.phone_number,
        });

        await initializeAndMakeCall({
          phoneNumber: entityData.phone_number,
          callParams: {
            From: fromPhoneNumber,
            To: entityData.phone_number,
          },
          entityDetails: entityData,
          campaignId: state.currentCampaign.campaignId,
          entityType: data[0].entity_type,
        });
        
        await dispatch("refreshCampaignDetails");
      }
    },

    async refreshCampaignDetails({ state, commit, dispatch }) {
      try {
        if (state.currentCampaign.campaignId) {
          const details = await toGetCallCampaignById(
            state.currentCampaign.campaignId,
          );
          eventBus.emit("reload-campaign-leads");
          if (details.status === callCampaignStatuses.COMPLETED) {
            await dispatch("endCampaign", details);
            commit("UPDATE_IS_ACTIVE", false);
            return;
          }
          commit("UPDATE_CAMPAIGN_DETAILS", details);
          commit("UPDATE_CAMPAIGN_STATUS", details.status);
        }
      } catch (error) {
        console.error(
          "An error occurred while refreshing campaign details: ",
          error,
        );
      }
    },

    async endCampaign({ commit, state, dispatch }, details) {
      try {
        await handleSkippedCalls();
        const campaignId = details?.id;
        const campaignType = details?.campaign_type;

        commit("SET_CURRENT_CAMPAIGN", {
          campaignId: campaignId,
          isActive: false,
          details: details,
          status: callCampaignStatuses.COMPLETED,
        });
        await dispatch("resetCampaignStates");
        if (!state.isCampaignPageOpen) {
          await dispatch("dynamicBanner/updateShowBanner", false, {
            root: true,
          });
        }
        const routeName = campaignType === dialerTypes.VOICE_AGENTS_DIALER
          ? "voice-agents-campaign-view"
          : "sales-dialer-campaign-detail";

        await router.push({
          name: routeName,
          params: { id: campaignId },
        });
      } catch (error) {
        console.error("An error occurred while ending campaign: ", error);
      }
    },

    async resetCampaignStates({ commit }) {
      try {
        commit("UPDATE_CALL_ONGOING", false);
        commit("SET_SKIP_COOLDOWN", false);
        commit("RESET_SKIPPED_CALLS");
        commit("UPDATE_IS_ACTIVE", false);
      } catch (error) {
        console.error(
          "An error occurred while resetting campaign states: ",
          error,
        );
      }
    },

    incrementSkippedCalls({ commit }) {
      commit("INCREMENT_SKIPPED_CALLS");
    },

    setCallOngoing({ commit }, value) {
      commit("UPDATE_CALL_ONGOING", value);
    },

    resetSkippedCalls({ commit }) {
      commit("RESET_SKIPPED_CALLS");
    },

    setIsRecordingEnabled({ commit }, status) {
      commit("SET_IS_RECORDING_ENABLED", status);
    },

    async setIsCampaignPageOpen({ commit, dispatch, state, rootState }, value) {
      commit("SET_IS_CAMPAIGN_PAGE_OPEN", value);
      if (
        !value &&
        !state.currentCampaign.isActive &&
        !rootState?.tasks?.autoPlaySessionId
      ) {
        await dispatch("dynamicBanner/updateShowBanner", false, { root: true });
      }
    },

    setSkipCooldown({ commit }, value) {
      commit("SET_SKIP_COOLDOWN", value);
    },

    updateCallOngoing({ commit }, value) {
      commit("UPDATE_CALL_ONGOING", value);
    },

    updateCampaignId({ commit }, value) {
      commit("UPDATE_CAMPAIGN_ID", value);
    },

    setDrawerVisible({ commit }, value) {
      commit("SET_DRAWER_VISIBLE", value);
    },

    updateCampaignActive({ commit }, value) {
      commit("UPDATE_IS_ACTIVE", value);
    },
    setCampaignCallSid({ commit }, value) {
      commit("SET_CAMPAIGN_CALL_SID", value);
    },
  },

  getters: {
    isCurrentCampaign: (state) => state.currentCampaign?.isActive,
    currentCampaignId: (state) => state.currentCampaign?.campaignId,
    campaignDetails: (state) => state.currentCampaign?.details,
    campaignStatus: (state) => state.currentCampaign?.status,
    skippedCallsCount: (state) => state.skippedCallsCount,
    isRecordingEnabled: (state) => state.isRecordingEnabled,
    campaignType: (state) => state.currentCampaign.details?.campaign_type,
    campaignCooldownTime: (state) =>
      state.currentCampaign.details?.cooldown_time,
    isCampaignPageOpen: (state) => state.isCampaignPageOpen,
    totalCallsAttempted: (state) =>
      state.currentCampaign?.details?.attempted_calls,
    totalCampaignCalls: (state) =>
      state.currentCampaign.details?.total_contacts || 0,
    isSkipCooldown: (state) => state.isSkipCooldown,
    callOngoing: (state) => state.callOngoing,
    isDrawerVisible: (state) => state.drawerVisible,
    campaignCallSid: (state) => state.campaignCallSid,
  },
};
